import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useUserStore } from "@/hooks/useUserStore";
import { useStores } from "@/stores";
import type { IUserProfile } from "@/stores/models";
import type { AuthProviderProps, AuthContextType } from "@/hooks/auth/types";
import { AuthContext } from "@/hooks/auth/AuthContext";

export const AuthProvider = observer(({ children }: AuthProviderProps) => {
  const rootStore = useStores();
  const auth = useAuthStore();
  const userStore = useUserStore();

  useEffect(() => {
    // AuthStore.loadSession will coordinate with UserStore internally
    auth.loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UserStore.updateProfile writes fields in place, so the node's identity never
  // changes and is useless as a memo dep. Depend on the projected fields one by one
  // rather than on the whole node's snapshot: the snapshot also covers fields this
  // context never exposes (tos_accepted_at, the opt-outs), and consumers use
  // `userProfile` as a raw effect dep — BuildingDocuments and DocumentsMainView
  // refetch documents on it — so an unprojected write must not change its identity.
  const {
    id,
    email,
    org_id,
    first_name,
    last_name,
    phone_number,
    job_title,
    company,
    avatar_url,
    color,
    allow_permanent_delete,
  }: Partial<IUserProfile> = userStore.userProfile ?? {};

  const userProfile = useMemo<AuthContextType["userProfile"]>(
    () =>
      id === undefined || email === undefined
        ? null
        : {
            id,
            email,
            org_id,
            first_name,
            last_name,
            phone_number,
            job_title,
            company,
            avatar_url,
            color,
            allow_permanent_delete,
          },
    [
      id,
      email,
      org_id,
      first_name,
      last_name,
      phone_number,
      job_title,
      company,
      avatar_url,
      color,
      allow_permanent_delete,
    ]
  );

  const value: AuthContextType = useMemo(
    () => ({
      userId: auth.userId,
      userProfile,
      orgRoles: auth.orgRoles.map((role) => ({
        id: role.id,
        user_id: role.user_id,
        org_id: role.org_id,
        role: role.role,
        created_at: role.created_at,
        updated_at: role.updated_at,
        status: role.status,
      })),
      hasAcceptedTos: userStore.hasAcceptedTos,
      isLoading: auth.isLoading || userStore.isLoading,
      isAuthenticated: auth.isAuthenticated,
      sessionError: auth.error || userStore.error,
      retryAuth: () => auth.loadSession(),
      signOut: () => auth.logout(),
    }),
    [
      auth.userId,
      userProfile,
      auth.orgRoles,
      userStore.hasAcceptedTos,
      auth.isLoading,
      userStore.isLoading,
      auth.isAuthenticated,
      auth.error,
      userStore.error,
      rootStore,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});
